// frontend/src/components/notifications/NotificationDrawer.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { Form, InputGroup, Badge, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import Drawer from '../../theme/components/Drawer';
import { useNotifications } from '../../contexts/NotificationContext';
import styles from './NotificationDrawer.module.scss';

/**
 * 🗂️ NotificationDrawer - Drawer de Todas as Notificações
 * 
 * Funcionalidades:
 * - Lista completa de notificações
 * - Filtros: Não Lidas / Lidas (inicial: Não Lidas)
 * - Busca por texto
 * - Botão ✓ para marcar como lida (apenas em não lidas)
 * - Clicar na notificação = Marcar como lida + Navegar
 * - Paginação: 20 notificações por página
 * - Notificações preservadas (nunca deletadas)
 */
const NotificationDrawer = ({ show, onHide }) => {
  const {
    notifications,
    unreadCount,
    markAsRead,
    navigateToNotification,
    getNotificationConfig
  } = useNotifications();

  const [filter, setFilter] = useState('unread'); // 'unread', 'read' (inicial: não lidas)
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Filtrar e buscar notificações
  const filteredNotifications = useMemo(() => {
    let filtered = notifications;

    // Aplicar filtro de status
    if (filter === 'unread') {
      filtered = filtered.filter(n => !n.read);
    } else if (filter === 'read') {
      filtered = filtered.filter(n => n.read);
    }

    // Aplicar busca por texto
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(n =>
        n.title?.toLowerCase().includes(searchLower) ||
        n.message?.toLowerCase().includes(searchLower)
      );
    }

    // Ordenar por timestamp (mais recente primeiro)
    return filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [notifications, filter, searchTerm]);

  // Calcular notificações paginadas
  const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);
  const paginatedNotifications = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredNotifications.slice(startIndex, endIndex);
  }, [filteredNotifications, currentPage, itemsPerPage]);

  // Resetar busca e página ao mudar filtro
  useEffect(() => {
    setSearchTerm('');
    setCurrentPage(1);
  }, [filter]);

  // Resetar página ao buscar
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Handler: Clicar na notificação
  const handleNotificationClick = async (notification) => {
    // Marcar como lida
    if (!notification.read) {
      await markAsRead(notification.id);
    }
    
    // Navegar para a página específica
    navigateToNotification(notification);
    
    // Fechar drawer após pequeno delay para UX
    setTimeout(() => {
      onHide();
    }, 300);
  };

  // Handler: Marcar como lida (ao clicar no X em notificações não lidas)
  const handleMarkAsRead = async (e, notificationId) => {
    e.stopPropagation(); // Evitar trigger do click no item
    await markAsRead(notificationId);
  };

  // Formatar timestamp relativo
  const formatRelativeTime = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'Agora mesmo';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}min atrás`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h atrás`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d atrás`;
    }
  };

  return (
    <Drawer
      show={show}
      onHide={onHide}
      title={
        <div className="d-flex align-items-center gap-2">
          <span>Notificações</span>
          {unreadCount > 0 && (
            <Badge bg="danger" pill style={{ fontSize: '0.7rem' }}>{unreadCount}</Badge>
          )}
        </div>
      }
      icon="mdi:bell"
      size="md"
    >
      {/* Filtros e Busca */}
      <Drawer.Section>
        {/* Filtros de Status */}
        <div className="d-flex gap-2 mb-3">
          <Button
            variant={filter === 'unread' ? 'primary' : 'outline-secondary'}
            size="sm"
            onClick={() => setFilter('unread')}
            className="flex-fill"
          >
            <Icon icon="mdi:bell-badge" className="me-1" width={16} />
            Não Lidas ({notifications.filter(n => !n.read).length})
          </Button>
          <Button
            variant={filter === 'read' ? 'primary' : 'outline-secondary'}
            size="sm"
            onClick={() => setFilter('read')}
            className="flex-fill"
          >
            <Icon icon="mdi:bell-check" className="me-1" width={16} />
            Lidas ({notifications.filter(n => n.read).length})
          </Button>
        </div>

        {/* Campo de Busca */}
        <InputGroup size="sm">
          <InputGroup.Text>
            <Icon icon="mdi:magnify" width={18} />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Buscar notificações..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <Button
              variant="outline-secondary"
              onClick={() => setSearchTerm('')}
            >
              <Icon icon="mdi:close" width={18} />
            </Button>
          )}
        </InputGroup>

      </Drawer.Section>

      <Drawer.Divider />

      {/* Lista de Notificações */}
      <Drawer.Section>
        {filteredNotifications.length === 0 ? (
          <Drawer.EmptyState
            icon="mdi:bell-off-outline"
            text={
              searchTerm
                ? 'Nenhuma notificação encontrada'
                : filter === 'unread'
                ? 'Nenhuma notificação não lida'
                : filter === 'read'
                ? 'Nenhuma notificação lida'
                : 'Você não tem notificações'
            }
          />
        ) : (
          <>
            <div className={styles.notificationList}>
              {paginatedNotifications.map((notification) => {
              const config = getNotificationConfig(notification.type);
              return (
                <div
                  key={notification.id}
                  className={`${styles.notificationItem} ${!notification.read ? styles.unread : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                  role="button"
                  tabIndex={0}
                >
                  {/* Indicador de não lida */}
                  {!notification.read && (
                    <div className={styles.unreadIndicator} />
                  )}

                  {/* Ícone */}
                  <div className={`${styles.notificationIcon} ${config.bgColor}`}>
                    <Icon icon={config.icon} width={20} color={`var(--bs-${config.color})`} />
                  </div>

                  {/* Conteúdo */}
                  <div className={styles.notificationContent}>
                    <div className={styles.notificationTitle}>
                      {notification.title}
                      <span className={styles.notificationTime}>
                        {formatRelativeTime(notification.timestamp)}
                      </span>
                    </div>
                    <div className={styles.notificationMessage}>
                      {notification.message}
                    </div>
                  </div>

                  {/* Botão de Marcar como Lida (apenas para não lidas) */}
                  {!notification.read && (
                    <button
                      className={styles.markReadBtn}
                      onClick={(e) => handleMarkAsRead(e, notification.id)}
                      title="Marcar como lida"
                    >
                      <Icon icon="mdi:check" width={18} />
                    </button>
                  )}
                </div>
              );
              })}
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
              <>
                <Drawer.Divider />
                <div className={styles.pagination}>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    <Icon icon="mdi:chevron-left" width={18} />
                    Anterior
                  </Button>

                  <span className={styles.paginationInfo}>
                    Página {currentPage} de {totalPages}
                    <span className="text-muted ms-2" style={{ fontSize: '0.85rem' }}>
                      ({filteredNotifications.length} {filter === 'unread' ? 'não lidas' : 'lidas'})
                    </span>
                  </span>

                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Próxima
                    <Icon icon="mdi:chevron-right" width={18} />
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </Drawer.Section>
    </Drawer>
  );
};

export default NotificationDrawer;

